import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";

// Compliance frameworks (CIS, NIST, PCI DSS, SPARK, ...) can be switched off per organization.
// The list of switched-on names comes from user-details (subscription_features.frameworks).
// Benchmarks and MITRE are never switched off, so use this only for framework-level UI.

// Fixed text for a framework that is switched off (the backend answers such requests with 403)
export const FRAMEWORK_NOT_ENABLED_MESSAGE =
  "This compliance framework is not enabled for your organization.";

// Per-device framework score keys ("<name>_<version>"), in the order used for defaults
export const FRAMEWORK_SCORE_KEYS = [
  "CIS_8",
  "CIS_7.1",
  "NIST_800-53 r4",
  "NIST_800-53 r5",
  "NIST_800-171 r2",
  "PCI DSS_4.0.1",
  "SPARK_1",
];

const normalizeName = (name: string) => name.trim().toUpperCase();

// The framework name of a score key: everything before the last "_" ("PCI DSS_4.0.1" -> "PCI DSS")
export const frameworkOfKey = (key: string): string => {
  const index = key.lastIndexOf("_");
  return index === -1 ? key : key.slice(0, index);
};

// No list (older backend, or not logged in again yet) means every framework is on
export const isFrameworkEnabled = (frameworks: unknown, name: string): boolean => {
  if (!Array.isArray(frameworks)) return true;
  const wanted = normalizeName(name);
  return frameworks.some((item) => typeof item === "string" && normalizeName(item) === wanted);
};

export const isFrameworkKeyEnabled = (frameworks: unknown, key: string): boolean =>
  isFrameworkEnabled(frameworks, frameworkOfKey(key));

// True when a request failed because its framework is switched off for the organization
export const isFrameworkNotEnabledError = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 403;

const useFrameworks = () => {
  const frameworks = useSelector(
    (state: RootState) => state.auth.subscriptionData?.frameworks
  );

  const isFrameworkOn = useCallback(
    (name: string) => isFrameworkEnabled(frameworks, name),
    [frameworks]
  );
  const isFrameworkKeyOn = useCallback(
    (key: string) => isFrameworkKeyEnabled(frameworks, key),
    [frameworks]
  );
  const enabledFrameworkKeys = useMemo(
    () => FRAMEWORK_SCORE_KEYS.filter((key) => isFrameworkKeyEnabled(frameworks, key)),
    [frameworks]
  );
  // First switched-on score key, for widgets that need one ("CIS_8" while CIS is on)
  const defaultFrameworkKey = enabledFrameworkKeys[0] ?? FRAMEWORK_SCORE_KEYS[0];

  return { isFrameworkOn, isFrameworkKeyOn, enabledFrameworkKeys, defaultFrameworkKey };
};

export default useFrameworks;
